import type { BoxProps } from '@hover/blueprint';
import {
  Box,
  Button,
  Image,
  motion,
  useBreakpointValue,
} from '@hover/blueprint';
import type { RefObject } from 'react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router';

import { PeekingCarousel } from 'src/modules/homeownerApp/components/PeekingCarousel';
import { getGeneratedImageLabel } from 'src/modules/homeownerApp/hooks';
import { Web } from 'src/shared/constants/FeatureFlag';
import type { AiDisplayImage, Maybe } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useTranslate,
  useTreatment,
} from 'src/shared/hooks';
import type { OptionalTrackingProperties } from 'src/shared/hooks/useTracking';
import { Route } from 'src/shared/route';
import type { ButtonImpression } from 'src/shared/telemetry';
import { buttonImpression } from 'src/shared/telemetry';
import { isEnabled } from 'src/shared/telemetry/featureFlag';

import { CarouselBadge } from '../../CarouselBadge';
import { FullscreenImageThumbnail } from '../../FullscreenImage';
import { useCurrentProject } from '../Menu/EditProject';

import { EmptyState } from './EmptyState';
import { ProjectEditButton } from './ProjectEditButton';
import { ProjectStylesButton } from './ProjectStylesButton';

interface ProjectCarouselProps extends BoxProps {
  addables: Maybe<AiDisplayImage[]> | undefined;
  projectId: string;
  projectNameVisibilityHandler?: RefObject<HTMLDivElement>;
  onActiveImageChange?: (image: AiDisplayImage) => void;
}

const MotionBox = motion(Box);

export const getProjectImages = (
  assets: Maybe<AiDisplayImage[]> | undefined,
) => {
  const images: {
    generatedImage: AiDisplayImage;
    src: string;
    edited: boolean;
    orientation?: string | null;
  }[] = [];

  assets?.forEach(asset => {
    images.push({
      generatedImage: asset,
      src:
        asset.aiGeneratedImage.generatedImagePath ||
        asset.aiGeneratedImage.imagePath,
      edited: !!asset.aiGeneratedImage.edits,
      orientation: asset?.aiOriginalImage?.orientation,
    });
  });

  return images;
};

const ProjectCarousel = ({
  projectId,
  projectNameVisibilityHandler,
  addables,
  onActiveImageChange,
  ...props
}: ProjectCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const isNewProjectUiEnabled = isEnabled(useTreatment(Web.NEW_PROJECT_UI));
  const isMobile = useBreakpointValue({ base: true, tablet: false });
  const carouselFooterAnimateObj = isMobile
    ? {
        viewport: { amount: 0.7 },
        whileInView: { opacity: 1 },
        initial: { opacity: 0 },
      }
    : { initial: { opacity: 1 }, animate: { opacity: 1 } };

  const images = useMemo(() => getProjectImages(addables) || [], [addables]);

  const history = useHistory();
  const { project } = useCurrentProject();

  // trigger the button impression only once when the image appears in the center
  const triggerImpression = useCallback(
    (optionalTrackingProperties: OptionalTrackingProperties) => {
      buttonImpression(optionalTrackingProperties as ButtonImpression);
    },
    [],
  );

  const { generatedImage: currentImage } = images?.[currentIndex] || {};

  useEffect(() => {
    onActiveImageChange?.(currentImage);
  }, [currentImage, onActiveImageChange]);

  const handleEditClick = (aiDisplayImageId: number) => {
    history.push(
      generatePath(Route.HOMEOWNER_APP_PROJECT_DISPLAY_FULLSCREEN.path, {
        projectId: project!.id,
        aiDisplayImageId,
      }),
    );
  };

  return (
    <PeekingCarousel
      data-testid="peeking-carousel"
      {...props}
      setCurrentIndex={setCurrentIndex}
    >
      {[
        ...images.map(({ src, generatedImage }) => (
          <FullscreenImageThumbnail
            flexDir="column"
            height="100%"
            image={generatedImage}
            key={generatedImage.id}
          >
            <Image
              alt={t('projects.projectCarouselImageAlt')}
              aspectRatio={{
                base:
                  generatedImage.aiOriginalImage?.orientation === 'horizontal'
                    ? 4 / 3
                    : 3 / 4,
                tablet: 'unset',
              }}
              borderRadius={400}
              cursor="pointer"
              data-id={generatedImage.id}
              data-testid="project-carousel-image"
              draggable={false}
              height={{ base: '245px', tablet: '496px' }}
              objectFit={{ base: 'cover', tablet: 'contain' }}
              onClick={() => handleEditClick(generatedImage.id)}
              src={src}
            />
            {/* Image header */}
            <MotionBox
              data-testid="project-carousel-image-header"
              left={500}
              position="absolute"
              top={500}
            >
              <CarouselBadge
                label={getGeneratedImageLabel(generatedImage, t)}
              />
            </MotionBox>
            {/* Image footer */}
            <MotionBox
              alignItems="center"
              bottom={0}
              data-testid="project-carousel-image-footer"
              justifyContent="center"
              overflowX="visible"
              pb={{ base: 700, tablet: 500 }}
              pointerEvents="none"
              position={{ base: 'relative', tablet: 'absolute' }}
              pt={{ base: 500, tablet: 'unset' }}
              ref={projectNameVisibilityHandler}
              width="100%"
            >
              <Button
                display={{ base: 'block', tablet: 'flex' }}
                pointerEvents="auto"
                size="large"
                visibility="hidden"
              />
              <MotionBox
                bottom={{ base: 'unset', tablet: 600 }}
                gap={200}
                position="absolute"
                {...carouselFooterAnimateObj}
              >
                <ProjectEditButton
                  generatedImage={generatedImage}
                  pointerEvents="auto"
                  projectId={projectId}
                  size="large"
                  triggerImpression={triggerImpression}
                />
                {isNewProjectUiEnabled && generatedImage?.aiOriginalImageId && (
                  <ProjectStylesButton
                    generatedImage={generatedImage}
                    pointerEvents="auto"
                    projectId={projectId}
                    size="large"
                  />
                )}
              </MotionBox>
            </MotionBox>
          </FullscreenImageThumbnail>
        )),
        <EmptyState
          key="empty-state"
          text={images.length ? t('projects.addAPhoto') : undefined}
        />,
      ]}
    </PeekingCarousel>
  );
};

export { ProjectCarousel };
